import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { nodeButtons } from '@/lib/node-buttons';
import { type XYPosition, useReactFlow } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { memo, useMemo, useEffect, useRef } from 'react';
import { NodeLayout } from './layout';
import { useI18n } from '@/providers/i18n';
import { areNodePropsEqual } from './are-node-props-equal';

type DropNodeProps = {
  data: {
    isSource?: boolean;
    position: XYPosition;
    insertBetween?: { sourceId: string; targetId: string; edgeId?: string };
  };
  id: string;
};

export const DropNode = memo(({ data, id }: DropNodeProps) => {
  const { t } = useI18n();
  const { addNodes, deleteElements, getNode, addEdges, getNodeConnections } =
    useReactFlow();
  const ref = useRef<HTMLDivElement>(null);

  const buttonLookup = useMemo(() => {
    const map: Record<string, (typeof nodeButtons)[number]> = {};
    for (const button of nodeButtons) {
      map[button.id] = button as unknown as (typeof nodeButtons)[number];
    }
    return map;
  }, [nodeButtons]);

  const descriptions = useMemo(
    () => ({
      prompt: t('Create prompt'),
      llm: t('LLM'),
      text: t('Create prompt'),
      image: t('Upload image'),
      audio: t('Upload audio'),
      video: t('Upload video'),
      file: t('Upload File'),
      upload: t('Upload Assets'),
      tool: t('Tools'),
    }),
    [t]
  );

  const contextOptions = useMemo(() => {
    const result: Array<{ id: string; label: string; description?: string }> = [];
    for (const button of nodeButtons) {
      if (button.id === 'tweet') continue;
      if (button.id === 'upload') {
        // 将“上传素材”拆分成三项：上传图片/上传视频/上传音频
        result.push(
          {
            id: 'upload_image',
            label: t('Upload Image'),
            description: t('Upload image'),
          },
          {
            id: 'upload_video',
            label: t('Upload Video'),
            description: t('Upload video'),
          },
          {
            id: 'upload_audio',
            label: t('Upload Audio'),
            description: t('Upload audio'),
          }
        );
        continue;
      }
      result.push({
        id: button.id,
        label: t(button.label),
        description: descriptions[button.id as keyof typeof descriptions],
      });
    }
    return result;
  }, [descriptions, nodeButtons, t]);

  const connectionOptions = useMemo(() => {
    const config = [
      {
        id: 'text',
        label: t('Text'),
        description: t('LLM Node'),
      },
      {
        id: 'image',
        label: t('Image'),
        description: t('Image Model Node'),
      },
      {
        id: 'audio',
        label: t('Audio'),
        description: t('Audio Model Node'),
      },
      {
        id: 'video',
        label: t('Video'),
        description: t('Video Model Node'),
      },
    ] as const;

    return config;
  }, [t]);

  const isConnectionMode = typeof data?.isSource === 'boolean';
  const options = isConnectionMode ? connectionOptions : contextOptions;
  const layoutTitle = isConnectionMode
    ? t('Continue your flow')
    : t('Add a new node');
  const groupHeading = isConnectionMode
    ? t('Suggested next steps')
    : t('Add node');
  const connectionHint = isConnectionMode
    ? data?.isSource
      ? t('Select a node to connect from this source.')
      : t('Select a node to connect into this target.')
    : null;

  const handleSelect = (type: string, options?: Record<string, unknown>) => {
    // Get the position of the current node
    const currentNode = getNode(id);
    const position = currentNode?.position || { x: 0, y: 0 };
    const sourceNodes = getNodeConnections({
      nodeId: id,
    });

    // Delete the drop node
    deleteElements({
      nodes: [{ id }],
    });

    const newNodeId = nanoid();
    const { data: nodeData, ...rest } = options ?? {};

    // prompt 和 llm 都映射到 text 类型（通过 viewMode 区分）
    // upload_*（上传）映射到 upload 节点类型
    const isUploadVariant = type.startsWith('upload_');
    const nodeType = type === 'prompt' || type === 'llm' ? 'text' : (isUploadVariant ? 'upload' : type);

    // 设置默认 viewMode：
    // - 非 text：默认 transform
    // - text：
    //    - 连接模式选择的 text 表示 LLM => transform
    //    - 上下文菜单选择 llm => transform
    //    - 上下文菜单选择 prompt => primitive（不设置）
    // 若已显式提供 viewMode，尊重用户设置
    const finalData: Record<string, unknown> = { ...(nodeData ? nodeData : {}) };
    // upload_* 补齐 fileType
    if (isUploadVariant && !('fileType' in finalData)) {
      if (type === 'upload_image') finalData.fileType = 'image';
      else if (type === 'upload_video') finalData.fileType = 'video';
      else if (type === 'upload_audio') finalData.fileType = 'audio';
    }
    const hasExplicitViewMode = 'viewMode' in finalData;
    if (!hasExplicitViewMode) {
      if (nodeType !== 'text') {
        finalData.viewMode = 'transform';
      } else {
        const chosenIsLLM = type === 'llm' || isConnectionMode;
        if (chosenIsLLM) {
          finalData.viewMode = 'transform';
        }
      }
    }

    // Add the new node of the selected type
    addNodes({
      id: newNodeId,
      type: nodeType,
      position,
      data: finalData,
      origin: [0, 0.5],
      ...rest,
    });

    // If this Drop 节点用于在现有边之间插入新节点
    if (data?.insertBetween && data.insertBetween.sourceId && data.insertBetween.targetId) {
      const { sourceId, targetId, edgeId } = data.insertBetween;
      // 删除原有边（如果提供了具体 edgeId 则按 id 删除，否则按 source/target 匹配删除）
      if (edgeId) {
        deleteElements({ edges: [{ id: edgeId }] });
      } else {
        try {
          // 兜底：删除任意一条从 source 到 target 的边
          // 注意：该操作可能会删除多条平行边中的一条
          deleteElements({
            edges: sourceNodes
              .filter((e: any) => e.source === sourceId && (e as any).target === targetId)
              .map((e: any) => ({ id: e.id })),
          });
        } catch {}
      }
      // 添加两条新边：source -> newNode -> target
      addEdges({ id: nanoid(), source: sourceId, target: newNodeId, type: 'animated' });
      addEdges({ id: nanoid(), source: newNodeId, target: targetId, type: 'animated' });
      return;
    }

    // 默认：根据与 Drop 节点的连线方向进行连接
    for (const sourceNode of sourceNodes) {
      addEdges({
        id: nanoid(),
        source: data.isSource ? newNodeId : sourceNode.source,
        target: data.isSource ? sourceNode.source : newNodeId,
        type: 'animated',
      });
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Delete the drop node when Escape is pressed
        deleteElements({
          nodes: [{ id }],
        });
      }
    };

    const handleClick = (event: MouseEvent) => {
      // Get the DOM element for this node
      const nodeElement = ref.current;

      // Check if the click was outside the node
      if (nodeElement && !nodeElement.contains(event.target as Node)) {
        deleteElements({
          nodes: [{ id }],
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    setTimeout(() => {
      window.addEventListener('click', handleClick);
    }, 50);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('click', handleClick);
    };
  }, [deleteElements, id]);

  return (
    <div ref={ref}>
      <NodeLayout id={id} data={data} type="drop" title={layoutTitle}>
        <Command className="rounded-lg">
          <CommandList className="max-h-none overflow-visible">
            <CommandEmpty>{t('No results found.')}</CommandEmpty>
            <CommandGroup
              heading={groupHeading}
              className={
                isConnectionMode
                  ? "[&_[cmdk-group-heading]]:text-foreground [&_[cmdk-group-heading]]:text-base [&_[cmdk-group-heading]]:font-semibold"
                  : undefined
              }
            >
              {connectionHint && (
                <div className="text-muted-foreground text-xs px-1.5 pb-1.5">
                  {connectionHint}
                </div>
              )}
              <div className="grid grid-cols-2 gap-1 p-1">
                {options.map((option) => {
                  // 对于 connectionOptions，text 节点应该使用 llm 的图标
                  // 对于“上传”拆分项，映射到对应媒体类型图标
                  const iconId =
                    option.id === 'text'
                      ? 'llm'
                      : option.id === 'upload_image'
                      ? 'image'
                      : option.id === 'upload_video'
                      ? 'video'
                      : option.id === 'upload_audio'
                      ? 'audio'
                      : option.id;
                  const meta = buttonLookup[iconId];
                  const Icon = meta?.icon as React.ComponentType<{ size?: number; className?: string }> | undefined;
                  
                  return (
                    <CommandItem
                      key={option.id}
                      onSelect={() =>
                        handleSelect(
                          option.id,
                          option.id === 'upload_image'
                            ? { data: { fileType: 'image' } }
                            : option.id === 'upload_video'
                            ? { data: { fileType: 'video' } }
                            : option.id === 'upload_audio'
                            ? { data: { fileType: 'audio' } }
                            : undefined
                        )
                      }
                      className="flex items-start gap-2 p-2 rounded-md h-auto"
                    >
                      {Icon ? <Icon size={16} className="mt-0.5 shrink-0" /> : null}
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="font-medium text-sm truncate">{option.label}</span>
                        {option.description && (
                          <span className="text-muted-foreground text-xs line-clamp-1">
                            {option.description}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  );
                })}
              </div>
            </CommandGroup>
          </CommandList>
        </Command>
      </NodeLayout>
    </div>
  );
}, areNodePropsEqual);
DropNode.displayName = 'DropNode';
